import { ChevronDownIcon, ChevronRightIcon, PlayIcon, ZapIcon } from '@systemeio/icons'
import AddActionModal from 'modules/automation-rules/actions/components/add-action-modal'
import EditActions from 'modules/automation-rules/actions/components/edit-actions'
import { actionItems } from 'modules/automation-rules/actions/constants/action-items'
import { useAllowedActions } from 'modules/automation-rules/actions/hooks/use-allowed-actions'
import { getDefaultActionValue } from 'modules/automation-rules/actions/utils/get-default-action-value'
import { RuleCard } from 'modules/automation-rules/components/rule-card'
import AddTriggerModal from 'modules/automation-rules/triggers/components/add-trigger-modal'
import EditTriggers, {
  EditTriggersProps,
} from 'modules/automation-rules/triggers/components/edit-triggers'
import { triggerItems } from 'modules/automation-rules/triggers/constants/trigger-items'
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum'
import { useAllowedTriggers } from 'modules/automation-rules/triggers/hooks/use-allowed-triggers'
import { AutomationRulesTriggersEditType } from 'modules/automation-rules/triggers/types/automation-rule-triggers-edit-interface'
import { getDefaultTriggerValue } from 'modules/automation-rules/triggers/utils/get-default-trigger-value'
import {
  AutomationRuleErrorInterface,
  AutomationRuleViewInterface,
} from 'modules/automation-rules/types/automation-rule-interface'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface RuleProps extends Pick<EditTriggersProps, 'hideTriggers'> {
  tempState: AutomationRuleViewInterface
  setTempState: Dispatch<SetStateAction<AutomationRuleViewInterface>>
  errors: AutomationRuleErrorInterface
  maxTriggerId: React.MutableRefObject<number>
  maxActionId: React.MutableRefObject<number>
  isPreFetching?: boolean
  isFetching: boolean
  customAddTriggerHandler?: (
    trigger: AutomationRuleTriggersEnum,
    maxTriggerId: React.MutableRefObject<number>,
  ) => AutomationRulesTriggersEditType
  customTriggerTitle?: React.ReactNode
  allowedTriggers?: AutomationRuleTriggersEnum[]
  isStaticTriggers?: boolean
}

export function Rule({
  isPreFetching,
  isFetching,
  maxActionId,
  maxTriggerId,
  tempState,
  setTempState,
  errors,
  customAddTriggerHandler,
  customTriggerTitle,
  allowedTriggers,
  isStaticTriggers,
  hideTriggers = [],
}: RuleProps) {
  const { t } = useLocoTranslation()

  const [isAddTriggerOpened, setIsAddTriggerOpened] = useState(false)
  const [isAddActionOpened, setIsAddActionOpened] = useState(false)

  const finalAllowedTriggers = useAllowedTriggers(allowedTriggers)
  const allowedActions = useAllowedActions()

  const availableTriggerItems = triggerItems.filter(trigger =>
    finalAllowedTriggers.includes(trigger.slug),
  )
  const availableActionItems = actionItems.filter(action => allowedActions.includes(action.slug))

  const isAddDisabled = isFetching || Boolean(isPreFetching)

  const visibleTriggers = tempState.triggers.filter(trigger => !hideTriggers.includes(trigger.id))

  const addTriggerLabel = t('dashboard.automation_rule.edit.add_trigger')
  const addActionLabel = t('dashboard.automation_rule.edit.add_action')

  return (
    <div className={'tablet:flex-row tablet:items-start desktop:gap-5 flex flex-col gap-3'}>
      <RuleCard
        addLabel={addTriggerLabel}
        dataTest={'automation-rule-trigger-card'}
        description={t('dashboard.automation_rule.edit.when_happens')}
        icon={<ZapIcon />}
        isAddDisabled={isAddDisabled}
        isEmpty={visibleTriggers.length === 0}
        isLoading={isPreFetching}
        onAdd={availableTriggerItems.length === 0 ? undefined : () => setIsAddTriggerOpened(true)}
        title={customTriggerTitle ?? t('dashboard.automation_rule.trigger.title')}
        tone={'primary'}
      >
        <EditTriggers
          errors={errors}
          hideTriggers={hideTriggers}
          isStatic={isStaticTriggers}
          setRules={setTempState}
          triggers={tempState.triggers}
        />
      </RuleCard>

      <div
        aria-hidden="true"
        className={'tablet:self-stretch flex shrink-0 items-center justify-center self-center'}
      >
        <span
          className={
            'border-border-200 bg-bg-primary text-icon-primary flex size-10 items-center justify-center rounded-full border'
          }
        >
          <ChevronDownIcon className={'tablet:hidden size-[22px]'} focusable="false" />
          <ChevronRightIcon className={'tablet:block hidden size-[22px]'} focusable="false" />
        </span>
      </div>

      <RuleCard
        addLabel={addActionLabel}
        dataTest={'automation-rule-action-card'}
        description={t('dashboard.automation_rule.edit.then_happens')}
        icon={<PlayIcon />}
        isAddDisabled={isAddDisabled}
        isEmpty={tempState.actions.length === 0}
        isLoading={isPreFetching}
        onAdd={availableActionItems.length === 0 ? undefined : () => setIsAddActionOpened(true)}
        title={t('dashboard.automation_rule.action.title')}
        tone={'lightGreen'}
      >
        <EditActions actions={tempState.actions} errors={errors} setRules={setTempState} />
      </RuleCard>

      <AddTriggerModal
        items={availableTriggerItems}
        onAddTrigger={trigger =>
          setTempState(prev => ({
            ...prev,
            triggers: [
              ...prev.triggers,
              customAddTriggerHandler
                ? customAddTriggerHandler(trigger, maxTriggerId)
                : getDefaultTriggerValue(trigger, maxTriggerId),
            ],
          }))
        }
        onClose={() => setIsAddTriggerOpened(false)}
        opened={isAddTriggerOpened}
      />
      <AddActionModal
        items={availableActionItems}
        onAddAction={action =>
          setTempState(prev => ({
            ...prev,
            actions: [...prev.actions, { ...getDefaultActionValue(action, maxActionId) }],
          }))
        }
        onClose={() => setIsAddActionOpened(false)}
        opened={isAddActionOpened}
      />
    </div>
  )
}
